import { api } from '@xtnd/web/shared/api';

export interface WebhookEvent {
  id: string;
  tenantId: string;
  model: string;
  eventType: string;
  objectId: string;
  status: 'received' | 'processing' | 'processed' | 'failed';
  receivedAt: string;
  processedAt?: string;
  errorMessage?: string;
  retryCount: number;
  payload: {
    data: {
      id: number;
      etag?: string;
      type?: string;
      state?: string;
      [key: string]: any;
    };
    meta: {
      event: string;
      webhook_id: number;
      [key: string]: any;
    };
  };
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookEventStats {
  totalEvents: number;
  byStatus: {
    received: number;
    processing: number;
    processed: number;
    failed: number;
  };
  byModel: Record<string, number>;
  byEventType: Record<string, number>;
  recentActivity: {
    last24Hours: number;
    lastHour: number;
  };
}

export interface WebhookEventQuery {
  model?: string;
  eventType?: string;
  objectId?: string;
  status?: 'received' | 'processing' | 'processed' | 'failed';
  startDate?: string;
  endDate?: string;
  limit?: number;
  nextToken?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface WebhookEventQueryResult {
  events: WebhookEvent[];
  nextToken?: string;
  totalCount: number;
  hasMore: boolean;
}

class WebhookEventsApi {
  private readonly baseSource = '/webhook-events';

  /**
   * Query webhook events with pagination and filtering
   */
  async queryWebhookEvents(query: WebhookEventQuery = {}): Promise<WebhookEventQueryResult> {
    const params = new URLSearchParams();

    if (query.model) params.append('model', query.model);
    if (query.eventType) params.append('eventType', query.eventType);
    if (query.objectId) params.append('objectId', query.objectId);
    if (query.status) params.append('status', query.status);
    if (query.startDate) params.append('startDate', query.startDate);
    if (query.endDate) params.append('endDate', query.endDate);
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.nextToken) params.append('nextToken', query.nextToken);
    if (query.sortField) params.append('sortField', query.sortField);
    if (query.sortDirection) params.append('sortDirection', query.sortDirection);

    return api.get<WebhookEventQueryResult>(`${this.baseSource}?${params.toString()}`);
  }

  /**
   * Get webhook event statistics
   */
  async getWebhookEventStats(): Promise<WebhookEventStats> {
    return api.get<WebhookEventStats>(`${this.baseSource}/stats`);
  }

  /**
   * Get a specific webhook event by ID
   */
  async getWebhookEvent(eventId: string): Promise<WebhookEvent> {
    return api.get<WebhookEvent>(`${this.baseSource}/${eventId}`);
  }

  /**
   * Retry a failed webhook event
   */
  async retryWebhookEvent(eventId: string): Promise<WebhookEvent> {
    return api.post<WebhookEvent>(`${this.baseSource}/${eventId}/retry`);
  }

  /**
   * Get unique model types for filtering
   */
  async getModelTypes(): Promise<string[]> {
    const response = await api.get<{ models: string[] }>(`${this.baseSource}/models`);
    return response.models;
  }

  /**
   * Get unique event types for filtering
   */
  async getEventTypes(): Promise<string[]> {
    const response = await api.get<{ eventTypes: string[] }>(`${this.baseSource}/event-types`);
    return response.eventTypes;
  }
}

export const webhookEventsApi = new WebhookEventsApi();

